import React, { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { useTenant } from "./TenantContext";
import { clearPersistedViews } from '../hooks/usePersistedView';
import PushNotificationService from "../services/pushNotifications";
import WebSocketService from "../services/websocket";
import CacheManager from "../services/cacheManager";
import { appliquerPreferenceLangue } from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const { tenantSlug, isSuperAdmin } = useTenant();

  // Fonctions utilitaires pour le localStorage avec préfixe tenant
  const getStorageKey = (key) => {
    return tenantSlug ? `${tenantSlug}_${key}` : key;
  };
  
  const getItem = (key) => {
    return localStorage.getItem(getStorageKey(key));
  };
  
  const setItem = (key, value) => {
    localStorage.setItem(getStorageKey(key), value);
  };
  
  const removeItem = (key) => {
    localStorage.removeItem(getStorageKey(key));
  };

  // Restaurer le tenant depuis le localStorage au démarrage - PRIORITÉ HAUTE
  // Cette restauration doit se faire IMMÉDIATEMENT pour éviter le double refresh
  useEffect(() => {
    if (tenantSlug) {
      const storedTenant = getItem('tenant');
      if (storedTenant && !tenant) {
        try {
          const parsedTenant = JSON.parse(storedTenant);
          if (parsedTenant) {
            setTenant(parsedTenant);
          }
        } catch (e) {
          // Erreur silencieuse - le tenant sera rechargé depuis l'API
        }
      }
    }
  }, [tenantSlug]);
  
  // Synchroniser le tenant + token entre onglets (rafraîchissement externe).
  // Audit Auth (mai 2026) : ajout du sync token — avant, si un onglet faisait
  // logout, les autres onglets gardaient leur session active (token supprimé
  // du storage mais user toujours en state React). Risque : un onglet
  // "fantôme" continue à parler à l'API avec un token devenu invalide.
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (!tenantSlug) return;
      // Tenant mis à jour dans un autre onglet → propager au state
      if (e.key === getStorageKey('tenant') && e.newValue) {
        try {
          const updatedTenant = JSON.parse(e.newValue);
          setTenant(updatedTenant);
        } catch (err) {
          console.error('Erreur parsing tenant mis à jour:', err);
        }
      }
      // Token supprimé dans un autre onglet (logout cross-tab) → nettoyer ici aussi
      if (e.key === getStorageKey('token') && e.newValue === null) {
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setTenant(null);
        // Pas de redirect ici (l'autre onglet a déjà géré), juste reset state
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [tenantSlug]);

  useEffect(() => {
    // Ne pas vérifier le token si pas de tenantSlug (en attente du slug)
    if (!tenantSlug) {
      setLoading(false);
      return;
    }
    
    const token = getItem('token');
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Construire l'URL correcte selon le type d'utilisateur
      const meUrl = isSuperAdmin 
        ? `${API}/admin/auth/me` 
        : `${API}/${tenantSlug}/auth/me`;
      
      // Charger le tenant en parallèle avec la vérification du token
      // pour éviter le délai d'affichage des modules
      const loadTenantPromise = (async () => {
        // Vérifier d'abord si le tenant est déjà en cache/state
        const cachedTenant = getItem('tenant');
        if (cachedTenant && !tenant) {
          try {
            const parsed = JSON.parse(cachedTenant);
            if (parsed) setTenant(parsed);
          } catch (e) { /* ignore */ }
        }
        
        // Puis rafraîchir depuis l'API en arrière-plan
        try {
          const tenantResponse = await axios.get(`${API}/admin/tenants/by-slug/${tenantSlug}`);
          setTenant(tenantResponse.data);
          setItem('tenant', JSON.stringify(tenantResponse.data));
        } catch (error) {
          console.error('Erreur récupération tenant:', error);
        }
      })();
      
      // Verify token and get user info
      axios.get(meUrl)
        .then(async response => {
          // Attendre que le tenant soit chargé avant de définir l'utilisateur
          await loadTenantPromise;
          setUser(response.data);
          // Copie du profil : permet de rouvrir l'application sans réseau (voir catch).
          try { setItem('user', JSON.stringify(response.data)); } catch (e) { /* quota */ }
          setLoading(false);
          
          // Initialiser WebSocket pour les utilisateurs avec token en cache
          if (!isSuperAdmin && tenantSlug && response.data?.id) {
            try {
              WebSocketService.connect(tenantSlug, response.data.id);
              console.log('✅ WebSocket initialized (from cached token)');
            } catch (error) {
              console.error('⚠️ WebSocket initialization failed:', error);
            }
          }
        })
        .catch(async (error) => {
          // Serveur injoignable (pas de réseau, ou 502/503/504 du service worker /
          // de la passerelle) : ce n'est PAS un jeton invalide. On garde la session
          // avec le dernier profil connu, sinon un pompier qui rouvre l'application
          // sans réseau serait déconnecté et perdrait l'accès à ses visites préparées.
          const status = error?.response?.status;
          const injoignable = !error?.response || [502, 503, 504].includes(status);
          const profil = getItem('user');
          if (injoignable && profil) {
            try {
              await loadTenantPromise;
              setUser(JSON.parse(profil));
              setLoading(false);
              return;
            } catch (e) { /* profil illisible : comportement habituel ci-dessous */ }
          }
          console.log('Token invalide ou expiré, nettoyage...');
          // Nettoyer uniquement ce tenant
          removeItem('token');
          removeItem('user');
          removeItem('tenant');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [tenantSlug, isSuperAdmin]);

  // Finalise une connexion réussie : stocke le token, met à jour le state et
  // déclenche les effets de bord (push, websocket, cache). Partagé entre le
  // login normal et le login-2fa (2e étape TOTP) pour éviter toute dérive.
  const finalizeLogin = async (data) => {
    // Pour Super Admin, la réponse contient 'admin' au lieu de 'user'
    const { access_token, user: userData, admin: adminData, tenant } = data;
    const finalUserData = isSuperAdmin ? adminData : userData;

    setItem('token', access_token);

    if (tenant) {
      setItem('tenant', JSON.stringify(tenant));
      setTenant(tenant);
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    // « Connexion → toujours le tableau de bord » — TOUT ceci doit précéder
    // setUser : dès que user est posé, AppLayout et le module montent et
    // lisent currentPage/pathname/sessionStorage (setUser est flushé pendant
    // les await push/websocket plus bas).
    // 1) Purge des vues internes mémorisées (Prévention→Planification, etc.)
    //    — le simple REFRESH, lui, ne passe pas par finalizeLogin et les garde.
    clearPersistedViews();
    // 2+3) Module de départ = dashboard (clé lue par l'initializer d'App.js)
    //    + réalignement de l'URL (après logout, le pathname garde le dernier
    //    module et l'effet d'App.js le re-déduirait en priorité sur
    //    currentPage ; replaceState ne recharge pas la page).
    //    EXCEPTION deep-link : un lien courriel (/tenant?page=actifs&…) ou
    //    ?action=/?view= qui a forcé une reconnexion doit continuer d'aboutir
    //    à sa cible — on ne touche alors NI à l'URL NI à currentPage.
    const _params = new URLSearchParams(window.location.search);
    const _aDeepLink = _params.has('page') || _params.has('action') || _params.has('view');
    if (!_aDeepLink) {
      localStorage.setItem('currentPage', 'dashboard');
      if (!isSuperAdmin && tenantSlug) {
        window.history.replaceState(null, '', `/${tenantSlug}/dashboard`);
      }
    }

    setUser(finalUserData);
    // Copie du profil pour une réouverture sans réseau (voir la vérification du jeton).
    try { setItem('user', JSON.stringify(finalUserData)); } catch (e) { /* quota */ }

    // Initialiser push + websocket pour les utilisateurs non-super-admin
    if (!isSuperAdmin && tenantSlug && finalUserData?.id) {
      try {
        await PushNotificationService.initialize(tenantSlug, finalUserData.id);
        console.log('✅ Push notifications initialized');
      } catch (error) {
        console.error('⚠️ Push notifications initialization failed:', error);
      }
      try {
        WebSocketService.connect(tenantSlug, finalUserData.id);
        console.log('✅ WebSocket initialized');
      } catch (error) {
        console.error('⚠️ WebSocket initialization failed:', error);
      }
    }

    CacheManager.clearApiCache().then(() => {
      console.log('✅ Cache API invalidé après connexion');
    }).catch(() => {
      // Silently ignore if SW not ready
    });

    return { success: true };
  };

  const login = async (email, mot_de_passe) => {
    try {
      console.log('[AuthContext] Tentative de connexion pour:', email);

      // Nettoyer complètement avant une nouvelle connexion - uniquement pour ce tenant
      removeItem('token');
      removeItem('tenant');
      removeItem('user');
      delete axios.defaults.headers.common['Authorization'];

      const loginUrl = isSuperAdmin
        ? `${API}/admin/auth/login`
        : `${API}/${tenantSlug}/auth/login`;

      console.log('[AuthContext] URL de login:', loginUrl);

      const response = await axios.post(loginUrl, { email, mot_de_passe });

      // 2FA : mot de passe validé mais un code TOTP est requis. Ne rien stocker ;
      // remonter l'état au composant Login pour afficher la 2e étape.
      if (response.data?.twofa_required) {
        console.log('[AuthContext] 2FA requise');
        return {
          success: false,
          twofa_required: true,
          twofa_token: response.data.twofa_token,
        };
      }

      // 2FA forcée : la caserne EXIGE la 2FA et l'utilisateur ne l'a pas encore
      // configurée. Comme pour twofa_required : ne rien stocker, remonter l'état
      // au composant Login pour afficher l'écran d'enrôlement obligatoire.
      if (response.data?.twofa_setup_required) {
        console.log('[AuthContext] Enrôlement 2FA requis');
        return {
          success: false,
          twofa_setup_required: true,
          setup_token: response.data.setup_token,
        };
      }

      console.log('[AuthContext] Réponse login réussie');
      return await finalizeLogin(response.data);
    } catch (error) {
      console.error('[AuthContext] Erreur de connexion:', error.response?.status, error.response?.data);

      // Ne pas laisser un token ou header invalide trainer
      removeItem('token');
      delete axios.defaults.headers.common['Authorization'];

      return {
        success: false,
        error: error.response?.data?.detail || 'Erreur de connexion - Vérifiez vos identifiants',
      };
    }
  };

  // 2e étape de connexion pour les comptes 2FA : envoie le token intermédiaire
  // + le code TOTP, puis finalise comme un login normal.
  const loginAvec2FA = async (twofa_token, code) => {
    try {
      removeItem('token');
      delete axios.defaults.headers.common['Authorization'];

      const url = `${API}/${tenantSlug}/auth/login-2fa`;
      const response = await axios.post(url, { twofa_token, code });
      return await finalizeLogin(response.data);
    } catch (error) {
      console.error('[AuthContext] Erreur login-2fa:', error.response?.status, error.response?.data);
      removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      return {
        success: false,
        error: error.response?.data?.detail || 'Code invalide',
      };
    }
  };

  // Enrôlement 2FA forcé (la caserne exige la 2FA) — étape 1 : génère secret + QR.
  // Authentifié par le token d'enrôlement (dans le corps), comme login-2fa. Ne
  // finalise rien ; renvoie { secret, qr } au composant. Laisse l'erreur remonter
  // (le composant Login inspecte le détail pour son message).
  const enroll2FASetup = async (tenantSlug, setupToken) => {
    const response = await axios.post(
      `${API}/${tenantSlug}/auth/2fa/enroll/setup`,
      { setup_token: setupToken }
    );
    return response.data; // { secret, qr }
  };

  // Enrôlement 2FA forcé — étape 2 : vérifie le code, active la 2FA et délivre le
  // vrai token → finalise la connexion comme un login normal. Laisse l'erreur
  // remonter pour que le composant distingue « code invalide » de « session expirée ».
  const enroll2FAEnable = async (tenantSlug, setupToken, code) => {
    const response = await axios.post(
      `${API}/${tenantSlug}/auth/2fa/enroll/enable`,
      { setup_token: setupToken, code }
    );
    return await finalizeLogin(response.data); // { success: true }
  };

  const logout = async () => {
    // Déconnecter le WebSocket
    WebSocketService.disconnect();
    
    // Désenregistrer les notifications push
    PushNotificationService.unregister().catch(err => 
      console.error('Error unregistering push notifications:', err)
    );
    
    // Invalider le cache API pour éviter les données obsolètes
    CacheManager.clearApiCache().catch(() => {
      // Silently ignore if SW not ready
    });
    
    // NE PAS effacer les credentials sauvegardés ("Se souvenir de moi")
    // L'utilisateur pourra ainsi voir ses identifiants pré-remplis au prochain login
    // mais devra quand même cliquer sur "Se connecter"
    // Les credentials (email/mot de passe) sont conservés dans storage.js
    console.log('[Logout] Keeping saved credentials for convenience');
    
    // Nettoyer uniquement les données du tenant actuel (pas tous les tenants)
    removeItem('token');
    removeItem('tenant');
    removeItem('user');
    removeItem('current_inspection_id');
    removeItem('detail_inspection_id');
    
    // Nettoyage supplémentaire pour iOS - effacer les caches potentiellement corrompus
    try {
      // Nettoyer sessionStorage aussi (peut causer des problèmes sur iOS)
      if (tenantSlug) {
        sessionStorage.removeItem(`${tenantSlug}_token`);
        sessionStorage.removeItem(`${tenantSlug}_user`);
        sessionStorage.removeItem(`${tenantSlug}_tenant`);
      }
      // Nettoyer les anciens formats de stockage qui peuvent interférer
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
    } catch (e) {
      console.log('[Logout] SessionStorage cleanup skipped:', e.message);
    }
    
    // Supprimer le header Authorization d'axios
    delete axios.defaults.headers.common['Authorization'];
    
    // Réinitialiser l'état utilisateur
    setUser(null);
    setTenant(null);
    
    // Forcer le rafraîchissement de la page pour éviter les problèmes de cache
    setTimeout(() => {
      window.location.href = window.location.origin + window.location.pathname;
    }, 100);
  };

  /**
   * Recharge les infos du tenant depuis le backend.
   * Appelé par les pages de Paramètres après une sauvegarde pour propager
   * les changements (ex: recurrence_inspections par niveau de risque)
   * dans toute l'app sans rechargement de page.
   */
  const refreshTenant = async () => {
    if (!tenantSlug) return null;
    try {
      const response = await axios.get(`${API}/admin/tenants/by-slug/${tenantSlug}`);
      setTenant(response.data);
      setItem('tenant', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('refreshTenant erreur:', error);
      return null;
    }
  };

  // Langue choisie dans « Mon profil » (null/"auto" = langue de l'appareil).
  // Champ absent (ancien backend) : on garde la langue locale.
  const langueUtilisateur = user && 'langue' in user ? (user.langue || 'auto') : undefined;
  useEffect(() => {
    if (langueUtilisateur !== undefined) appliquerPreferenceLangue(langueUtilisateur);
  }, [langueUtilisateur]);

  return (
    <AuthContext.Provider value={{ user, setUser, tenant, login, loginAvec2FA, enroll2FASetup, enroll2FAEnable, logout, loading, refreshTenant }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
